import TelegramButton, { TelegramIcon } from "../components/TelegramButton";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { publicApi, type ReportWindow } from "../lib/api";
import { fmtCount, fmtMultiple, fmtPct, fmtSymbol, fmtUsd } from "../lib/format";
import { useT } from "../lib/i18n";
import { landingCopy } from "../lib/landing-copy";
import ChainIcon, { CHAIN_LABELS, SUPPORTED_CHAINS } from "../components/ChainIcon";
import AsyncPanel, { PanelSkeleton, useReducedMotion } from "../components/AsyncPanel";
import Logo from "../components/Logo";

function Arrow() { return <span aria-hidden="true">↗</span>; }
type Copy = typeof landingCopy.en;
type Lang = "en" | "tr";

// Sections settle in from a visible resting state; nothing waits at opacity 0.
function useLandingEntrance() {
  const root = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (!root.current || reducedMotion || !window.IntersectionObserver) return;
    const animations: Animation[] = [];
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-revealed");
        if (entry.target.animate) animations.push(entry.target.animate(
          [{ opacity: .6, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0)" }],
          { duration: 520, easing: "cubic-bezier(.22,1,.36,1)" },
        ));
        observer.unobserve(entry.target);
      }
    }, { threshold: .12 });
    root.current.querySelectorAll(".a1-section-head, .a1-board, .a1-report-body, .a1-workflow-grid, .a1-plans, .a1-research, .a1-final").forEach(el => observer.observe(el));
    return () => { observer.disconnect(); animations.forEach(animation => animation.cancel()); };
  }, [reducedMotion]);
  return root;
}

function fill(template: string, vars: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`));
}

function Hero({ c }: { c: Copy }) {
  return <section className="a1-container a1-hero">
    <div className="a1-hero-copy">
      <p className="a1-label">{c.eyebrow}</p>
      <h1>{c.title}<br/><span>{c.titleAccent}</span></h1>
      <p className="a1-hero-intro">{c.intro}</p>
      <div className="a1-actions">
        <TelegramButton source="hero">{c.start}</TelegramButton>
        <Link className="a1-button a1-button-secondary" to="/how-it-works">{c.guide}<span aria-hidden="true">→</span></Link>
      </div>
      <p className="a1-hero-note">{c.heroNote}</p>
    </div>
    <div className="a1-hero-mark" aria-hidden="true">
      <img src="/brand/metal-a-960.jpg" alt="" width="960" height="960" decoding="async"/>
    </div>
    <ol className="a1-hero-steps">
      {c.heroSteps.map((step, i) => <li key={step.title}><span className="a1-step-number">0{i + 1}</span><h2>{step.title}</h2><p>{step.body}</p></li>)}
    </ol>
  </section>;
}

function Networks({ c }: { c: Copy }) {
  return <section className="a1-container a1-networks" aria-label={c.networks}><div className="a1-networks-inner">
    <p className="a1-label">{c.networks}</p>
    <ul className="a1-chain-list">{SUPPORTED_CHAINS.map(chain => <li key={chain}><ChainIcon chain={chain} size={22}/><span>{CHAIN_LABELS[chain]}</span>{chain === "arc" && <sup aria-hidden="true">*</sup>}</li>)}</ul>
    <p className="a1-micro">{c.networkNote}</p>
  </div></section>;
}

function RunnerBoard({ c, lang }: { c: Copy; lang: Lang }) {
  const runners = useQuery({ queryKey: ["landing-runners"], queryFn: () => publicApi.tokens({ days: 7, sort: "peak", min_wallets: 3, limit: 5 }), staleTime: 300_000, retry: 1 });
  const items = runners.data?.items ?? [];
  const meta = fill(c.runnersMeta, { n: items.length || 5, h: runners.data?.delay_hours ?? 24 });

  return <section className="a1-container a1-section" id="runners">
    <div className="a1-section-head">
      <div><p className="a1-label">{c.runnersLabel}</p><h2>{c.runnersTitle}</h2><p className="a1-label a1-section-meta">{meta}</p></div>
      <Link className="a1-text-link" to="/tokens">{c.history}<Arrow/></Link>
    </div>
    <AsyncPanel busy={runners.isFetching || runners.isPending} hasData={!!runners.data && !runners.isError} error={runners.isError} loadingLabel={c.loading} updatingLabel={c.updating} transitionKey={runners.dataUpdatedAt} skeleton={<div className="a1-runner-skeleton"><PanelSkeleton rows={8}/></div>}>
      <div className="a1-board a1-spot">
        {runners.isError
          ? <div className="a1-data-state" role="status"><p>{c.unavailable}</p><button className="a1-button a1-button-secondary" onClick={() => runners.refetch()} disabled={runners.isFetching}>{c.retry}</button></div>
          : items.length === 0
            ? <div className="a1-data-state">{c.empty}</div>
            : <table className="a1-board-table">
                <caption className="sr-only">{c.runnersTitle}</caption>
                <thead><tr><th scope="col" className="a1-col-rank">#</th><th scope="col">{c.colToken}</th><th scope="col" className="a1-col-journey">{c.colJourney}</th><th scope="col" className="a1-col-multiple">{c.colMultiple}</th></tr></thead>
                <tbody>{items.map((item, i) => {
                  const journey = `${fmtUsd(item.mc_at_trigger, lang)} → ${fmtUsd(item.peak_mc, lang)}`;
                  return <tr key={`${item.chain}-${item.symbol}-${i}`} className={i === 0 ? "is-lead" : undefined}>
                    <td className="a1-col-rank">{String(i + 1).padStart(2, "0")}</td>
                    <th scope="row"><span className="a1-sym" title={fmtSymbol(item.symbol)}>{fmtSymbol(item.symbol)}</span><span className="a1-chain-with-icon a1-sym-chain"><ChainIcon chain={item.chain} size={16}/>{CHAIN_LABELS[item.chain] ?? item.chain}</span><span className="a1-journey-inline">{journey}</span></th>
                    <td className="a1-col-journey" aria-label={c.journey}>{journey}</td>
                    <td className="a1-col-multiple"><strong>{fmtMultiple(item.multiplier, lang)}</strong></td>
                  </tr>;
                })}</tbody>
              </table>}
      </div>
    </AsyncPanel>
    <div className="a1-foot">
      <p>{c.runnerShortNote}</p>
      <details className="a1-method"><summary>{c.method}<span aria-hidden="true">+</span></summary><p>{c.runnerNote}</p></details>
    </div>
  </section>;
}

function SignalReport({ c, lang }: { c: Copy; lang: Lang }) {
  const [span, setSpan] = useState<ReportWindow>("7d");
  const report = useQuery({ queryKey: ["signal-report", span], queryFn: () => publicApi.report(span), placeholderData: keepPreviousData, staleTime: 600_000, retry: 1 });
  const data = report.isError ? undefined : report.data;
  // An older backend without /report: leave the section out rather than show an error.
  if (report.isError && !report.isFetching && !report.data) return null;

  const pct = (n: number) => data && data.called > 0 ? fmtPct((n / data.called) * 100, 0, lang) : "";
  const day = (iso: string) => new Intl.DateTimeFormat(lang === "tr" ? "tr-TR" : "en-GB", { day: "numeric", month: "short" }).format(new Date(`${iso}T00:00:00Z`));
  const stats = data ? [
    { label: c.report2x, value: data.peaked_2x },
    { label: c.report5x, value: data.peaked_5x },
    { label: c.report10x, value: data.peaked_10x },
    { label: c.report50x, value: data.peaked_50x },
  ] : [];

  return <section className="a1-container a1-section a1-report" id="report">
    <div className="a1-section-head">
      <div><p className="a1-label">{c.reportLabel}</p><h2>{c.reportTitle}</h2><p className="a1-section-sub">{c.reportSub}</p></div>
      <div className="a1-seg" role="group" aria-label={c.reportWindow}>
        {(["7d", "30d"] as const).map(w => <button key={w} type="button" aria-pressed={span === w} onClick={() => setSpan(w)}>{w === "7d" ? c.report7 : c.report30}</button>)}
      </div>
    </div>
    <AsyncPanel busy={report.isFetching || report.isPending || report.isPlaceholderData} hasData={!!data} error={false} loadingLabel={c.reportLoading} updatingLabel={c.reportLoading} transitionKey={report.dataUpdatedAt} skeleton={<PanelSkeleton rows={6}/>}>
      {data && <div className="a1-report-body a1-spot">
        <div className="a1-report-main">
          <div className="a1-report-figure"><strong>{fmtCount(data.called, lang)}</strong><span className="a1-label">{c.reportCalled}</span></div>
          <div className="a1-report-side"><span className="a1-label">{c.reportNever}</span><strong>{fmtCount(data.never_ran, lang)}</strong><span>{pct(data.never_ran)}</span></div>
        </div>
        <dl className="a1-stats">
          {stats.map(stat => <div key={stat.label}><dt className="a1-label">{stat.label}</dt><dd><strong>{fmtCount(stat.value, lang)}</strong><span>{pct(stat.value)}</span></dd></div>)}
        </dl>
      </div>}
    </AsyncPanel>
    <div className="a1-foot">
      <p>{c.reportFoot}</p>
      {data && <p>{fill(c.reportRange, { from: day(data.since), to: day(data.until), h: data.delay_hours })}</p>}
    </div>
  </section>;
}

function SignalPreview({ c }: { c: Copy }) {
  const [replay, setReplay] = useState(0);
  return <div className="a1-product-stage">
    <div className="a1-product-caption"><span className="a1-label">{c.productLabel}</span><span className="a1-example-badge">{c.sample}</span></div>
    <div className="a1-signal-preview a1-spot" key={replay}>
      <div className="a1-signal-header"><Logo size={30}/><div><strong>AlphaHedge</strong><span>Telegram bot</span></div><TelegramIcon/></div>
      <div className="a1-signal-message">
        <div className="a1-signal-type"><span><i/> {c.signalType}</span><span className="a1-chain-with-icon"><ChainIcon chain="base" size={16}/>Base</span></div>
        <div className="a1-signal-token"><span className="a1-token-monogram" aria-hidden="true">T</span><div><h3>$TOKEN</h3><p>{c.sampleContext}</p></div></div>
        <dl className="a1-signal-fields"><div><dt>{c.sampleWallets}</dt><dd>3</dd></div><div><dt>{c.sampleMarketCap}</dt><dd>{c.sampleMarketValue}</dd></div><div><dt>{c.sampleLiquidity}</dt><dd>{c.sampleLiquidityValue}</dd></div><div><dt>{c.sampleAge}</dt><dd>{c.sampleAgeValue}</dd></div></dl>
        <div className="a1-signal-filter"><span>{c.sampleFilter}</span><strong>{c.sampleFilterValue}<span aria-hidden="true"> ✓</span></strong></div>
      </div>
      <p className="a1-signal-disclaimer">{c.sampleNote}</p>
    </div>
    <button className="a1-preview-replay" type="button" onClick={() => setReplay(value => value + 1)}><span aria-hidden="true">↻</span>{c.replay}</button>
  </div>;
}

function ConsensusDemo({ c }: { c: Copy }) {
  const [wallets, setWallets] = useState(3);
  const ready = wallets >= 3;
  return <div className={`a1-consensus a1-spot ${ready ? "is-ready" : ""}`}>
    <div className="a1-consensus-controls"><p id="consensus-control">{c.demoControl}</p><div className="a1-seg" role="group" aria-labelledby="consensus-control">{[1, 2, 3, 4, 5].map(n => <button type="button" key={n} aria-pressed={wallets === n} onClick={() => setWallets(n)}>{n}</button>)}</div></div>
    <div className="a1-consensus-diagram" key={wallets} aria-hidden="true"><div className="a1-wallet-nodes">{[1, 2, 3, 4, 5].map(n => <span key={n} className={n <= wallets ? "is-active" : ""}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="5" width="18" height="15" rx="3"/><path d="M3 8h18M16 12h5v4h-5z"/></svg></span>)}</div><div className="a1-consensus-connector"/><div className="a1-consensus-hub"><Logo size={38}/></div><div className="a1-consensus-connector"/><div className="a1-consensus-destinations"><span>{c.demoTelegram}</span><span>{c.demoAutomation}</span></div></div>
    <div className="a1-consensus-labels"><span>{c.demoWallets}</span><span>{c.demoThreshold}</span><span>AlphaHedge</span></div>
    <div className="a1-consensus-status" role="status"><span className="a1-status-dot"/><div><strong>{ready ? c.demoReady : c.demoWaiting}</strong><p>{ready ? c.demoReadyNote : c.demoWaitingNote}</p></div></div>
    <p className="a1-data-note">{c.demoFootnote}</p>
  </div>;
}

export default function Landing() {
  const entranceRef = useLandingEntrance();
  const { lang } = useT();
  const c = landingCopy[lang];

  return <div className="a1-landing" ref={entranceRef}>
    <Hero c={c}/>
    <Networks c={c}/>
    <RunnerBoard c={c} lang={lang}/>
    <SignalReport c={c} lang={lang}/>

    <section className="a1-section a1-workflow" id="how">
      <div className="a1-container">
        <div className="a1-section-head">
          <div><p className="a1-label">{c.demoLabel}</p><h2 className="a1-preserve-lines">{c.demoTitle}</h2><p className="a1-section-sub">{c.demoIntro}</p></div>
          <Link className="a1-text-link" to="/how-it-works">{c.guide}<Arrow/></Link>
        </div>
        <div className="a1-workflow-grid"><ConsensusDemo c={c}/><SignalPreview c={c}/></div>
      </div>
    </section>

    <section className="a1-container a1-section" id="plans">
      <div className="a1-section-head">
        <div><p className="a1-label">{c.plansLabel}</p><h2 className="a1-preserve-lines">{c.plansTitle}</h2></div>
        <p className="a1-section-sub">{c.plansShared}</p>
      </div>
      <div className="a1-plans a1-spot">
        <div className="a1-trial">
          <p className="a1-trial-number"><strong>04</strong><span className="a1-label">{c.trialUnit}</span></p>
          <h3>{c.trialTitle}</h3><p>{c.trialSub}</p>
          <TelegramButton source="trial">{c.trialCta}</TelegramButton>
        </div>
        <div className="a1-plan-options">
          <dl className="a1-stats a1-plan-grid">{c.planNames.map((name, i) => <div key={name}><dt className="a1-label">{name}</dt><dd><strong>{c.planDurations[i]}</strong></dd></div>)}</dl>
          <p className="a1-data-note">{c.plansNote}</p>
          <TelegramButton source="plans" variant="secondary">{c.planCta}</TelegramButton>
        </div>
      </div>
    </section>

    <section className="a1-container a1-section">
      <div className="a1-research">
        <div><p className="a1-label">{c.researchLabel}</p><h2 className="a1-preserve-lines">{c.researchTitle}</h2></div>
        <div><p>{c.researchBody}</p><Link className="a1-button a1-button-secondary" to="/strategies">{c.researchCta}<Arrow/></Link><p className="a1-micro">{c.researchNote}</p></div>
      </div>
    </section>

    <section className="a1-container a1-section a1-faq" id="faq">
      <div><p className="a1-label">{c.faqLabel}</p><h2>{c.faqTitle}</h2></div>
      <div>{c.faqs.map(faq => <details key={faq.q}><summary>{faq.q}<span aria-hidden="true">+</span></summary><p>{faq.a}</p></details>)}</div>
    </section>

    <section className="a1-container a1-final">
      <Logo size={44}/>
      <h2 className="a1-preserve-lines">{c.finalTitle}</h2>
      <p>{c.finalSub}</p>
      <TelegramButton source="final">{c.start}</TelegramButton>
      <details className="a1-brand-film" onToggle={event => { if (!event.currentTarget.open) event.currentTarget.querySelector("video")?.pause(); }}>
        <summary>{c.watchFilm}<span>{c.filmLength}</span></summary>
        <video controls playsInline preload="none" poster="/brand/video-poster.png" width="1920" height="1080" aria-label={c.videoLabel}><source src="/brand/rebrand.mp4" type="video/mp4"/><a href="/brand/rebrand.mp4">{c.videoLabel}</a></video>
      </details>
    </section>
  </div>;
}
